'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import type { Locales } from '@personly/libs-types';
import { toPathname } from '@personly/libs-pathnames';
import { useLocalizedPathname } from '@personly/libs-hooks';

export type Menu = {
  groupHandle: string;
  slug: string;
  title: string;
};

export { MenusProvider, useMenus };

export type Route = {
  active: boolean;
  groupHandle: string;
  href: string;
  title: string;
};

interface Props {
  children: ReactNode;
  locales: Locales;
  menu: Array<Menu>;
  initialOpen?: boolean;
}

type MenusContextType = {
  toggle: () => void;
  isOpen: boolean;
  routes: (pathname: string) => Array<Route>;
  matchEntry: (pathname: string) => Menu | undefined;
};

const MenusContext = createContext<MenusContextType | null>(null);

const MenusProvider = ({
  children,
  locales,
  menu,
  initialOpen = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const localizedPathname = useLocalizedPathname(locales);

  const matchEntry = useCallback(
    (pathname: string) =>
      menu.find(({ slug }) => {
        const currentPathname = localizedPathname(toPathname(slug));
        const regexPathname = currentPathname.replaceAll('/', '\\/');
        const regex = new RegExp(`^${regexPathname}(\\/.*)?$`);
        return regex.test(pathname);
      }),
    [localizedPathname, menu]
  );

  const makeRoute = useCallback(
    ({ slug, groupHandle, title }: Menu, active: boolean): Route => {
      const href = localizedPathname(toPathname(slug));
      return { active, groupHandle, href, title };
    },
    [localizedPathname]
  );

  const routes = useCallback(
    (pathname: string) => {
      const matchedEntry = matchEntry(pathname);

      return menu
        .filter(({ groupHandle }) =>
          matchedEntry ? groupHandle === matchedEntry.groupHandle : true
        )
        .reduce<Array<Route>>((acc, menuEntry) => {
          if (!matchedEntry) {
            const i = acc.findIndex(
              ({ groupHandle }) => groupHandle === menuEntry.groupHandle
            );

            if (i === -1 && !menuEntry.slug.includes('/'))
              acc.push(makeRoute(menuEntry, false));
            return acc;
          }

          if (menuEntry.slug.includes('/')) return acc;

          const active = matchedEntry.slug === menuEntry.slug;
          const route = makeRoute(menuEntry, active);
          return [...acc, route];
        }, [])
        .sort(sortRoutes);
    },
    [matchEntry, makeRoute, menu]
  );

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const value = { toggle, isOpen, routes, matchEntry };

  return (
    <MenusContext.Provider value={value}>{children}</MenusContext.Provider>
  );
};

function useMenus() {
  const context = useContext(MenusContext);
  if (!context) throw new Error('useMenus requires a context!');

  return context;
}

/**
 * Sort routes by title so that they are displayed alphabetically.
 *
 * @param {Route} lhs A route to compare for sorting
 * @param {Route} rhs A route to compare for sorting
 * @return An integer value corresponding to the new position of the route
 */
export const sortRoutes = (lhs: Route, rhs: Route) => {
  if (lhs.title > rhs.title) return 1;
  if (lhs.title < rhs.title) return -1;
  return 0;
};
